/**
 * Advanced path smoothing algorithms
 */

import { Point } from '../types';

/**
 * Create a smooth path using Catmull-Rom splines
 * This creates much smoother curves than quadratic bezier
 */
export function createCatmullRomPath(points: Point[], tension: number = 0.5): string {
  if (points.length < 2) return '';
  
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }
  
  const path: string[] = [];
  
  // Start at the first point
  path.push(`M ${points[0].x} ${points[0].y}`);
  
  // Handle the first segment specially
  const p0 = points[0];
  const p1 = points[1];
  const p2 = points[2];
  
  const cp1x = p0.x + (p1.x - p0.x) / 6;
  const cp1y = p0.y + (p1.y - p0.y) / 6;
  const cp2x = p1.x - (p2.x - p0.x) / 6;
  const cp2y = p1.y - (p2.y - p0.y) / 6;
  
  path.push(`C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p1.x} ${p1.y}`);
  
  // Draw Catmull-Rom splines for the middle points
  for (let i = 1; i < points.length - 2; i++) {
    const p0 = points[i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2];
    
    const cp1x = p1.x + (p2.x - p0.x) * tension / 6;
    const cp1y = p1.y + (p2.y - p0.y) * tension / 6;
    const cp2x = p2.x - (p3.x - p1.x) * tension / 6;
    const cp2y = p2.y - (p3.y - p1.y) * tension / 6;
    
    path.push(`C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`);
  }
  
  // Handle the last segment
  const lastIdx = points.length - 1;
  const p0Last = points[lastIdx - 2];
  const p1Last = points[lastIdx - 1];
  const p2Last = points[lastIdx];
  
  const cp1xLast = p1Last.x + (p2Last.x - p0Last.x) / 6;
  const cp1yLast = p1Last.y + (p2Last.y - p0Last.y) / 6;
  const cp2xLast = p2Last.x - (p2Last.x - p1Last.x) / 6;
  const cp2yLast = p2Last.y - (p2Last.y - p1Last.y) / 6;
  
  path.push(`C ${cp1xLast} ${cp1yLast}, ${cp2xLast} ${cp2yLast}, ${p2Last.x} ${p2Last.y}`);
  
  return path.join(' ');
}

/**
 * Create cubic bezier smooth path
 */
export function createCubicBezierPath(points: Point[]): string {
  if (points.length < 2) return '';
  
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }
  
  // Calculate control points
  const controlPoints = calculateCubicControlPoints(points);
  
  let path = `M ${points[0].x} ${points[0].y}`;
  
  for (let i = 0; i < points.length - 1; i++) {
    const cp1 = controlPoints.firstControlPoints[i];
    const cp2 = controlPoints.secondControlPoints[i];
    const endPoint = points[i + 1];
    
    path += ` C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${endPoint.x} ${endPoint.y}`;
  }
  
  return path;
}

/**
 * Calculate control points for cubic bezier curves
 */
function calculateCubicControlPoints(points: Point[]): {
  firstControlPoints: Point[];
  secondControlPoints: Point[];
} {
  const n = points.length - 1;
  const firstControlPoints: Point[] = [];
  const secondControlPoints: Point[] = [];
  
  if (n === 1) {
    // Special case: only 2 points
    firstControlPoints[0] = {
      x: (2 * points[0].x + points[1].x) / 3,
      y: (2 * points[0].y + points[1].y) / 3,
      pressure: points[0].pressure,
      timestamp: points[0].timestamp
    };
    
    secondControlPoints[0] = {
      x: 2 * firstControlPoints[0].x - points[0].x,
      y: 2 * firstControlPoints[0].y - points[0].y,
      pressure: points[1].pressure,
      timestamp: points[1].timestamp
    };
    
    return { firstControlPoints, secondControlPoints };
  }
  
  // Calculate first control points
  const a: number[] = [];
  const b: number[] = [];
  const c: number[] = [];
  const rX: number[] = [];
  const rY: number[] = [];
  
  // Set up the system of equations
  a[0] = 0;
  b[0] = 2;
  c[0] = 1;
  rX[0] = points[0].x + 2 * points[1].x;
  rY[0] = points[0].y + 2 * points[1].y;
  
  for (let i = 1; i < n - 1; i++) {
    a[i] = 1;
    b[i] = 4;
    c[i] = 1;
    rX[i] = 4 * points[i].x + 2 * points[i + 1].x;
    rY[i] = 4 * points[i].y + 2 * points[i + 1].y;
  }
  
  a[n - 1] = 2;
  b[n - 1] = 7;
  c[n - 1] = 0;
  rX[n - 1] = 8 * points[n - 1].x + points[n].x;
  rY[n - 1] = 8 * points[n - 1].y + points[n].y;
  
  // Solve the system using Thomas algorithm
  for (let i = 1; i < n; i++) {
    const m = a[i] / b[i - 1];
    b[i] = b[i] - m * c[i - 1];
    rX[i] = rX[i] - m * rX[i - 1];
    rY[i] = rY[i] - m * rY[i - 1];
  }
  
  firstControlPoints[n - 1] = {
    x: rX[n - 1] / b[n - 1],
    y: rY[n - 1] / b[n - 1],
    pressure: points[n - 1].pressure,
    timestamp: points[n - 1].timestamp
  };
  
  for (let i = n - 2; i >= 0; i--) {
    firstControlPoints[i] = {
      x: (rX[i] - c[i] * firstControlPoints[i + 1].x) / b[i],
      y: (rY[i] - c[i] * firstControlPoints[i + 1].y) / b[i],
      pressure: points[i].pressure,
      timestamp: points[i].timestamp
    };
  }
  
  // Calculate second control points
  for (let i = 0; i < n - 1; i++) {
    secondControlPoints[i] = {
      x: 2 * points[i + 1].x - firstControlPoints[i + 1].x,
      y: 2 * points[i + 1].y - firstControlPoints[i + 1].y,
      pressure: points[i + 1].pressure,
      timestamp: points[i + 1].timestamp
    };
  }
  
  secondControlPoints[n - 1] = {
    x: (points[n].x + firstControlPoints[n - 1].x) / 2,
    y: (points[n].y + firstControlPoints[n - 1].y) / 2,
    pressure: points[n].pressure,
    timestamp: points[n].timestamp
  };
  
  return { firstControlPoints, secondControlPoints };
}

/**
 * Simple moving average smoothing
 */
export function movingAverageSmooth(points: Point[], windowSize: number = 3): Point[] {
  if (points.length < windowSize) return points;
  
  const smoothed: Point[] = [];
  const halfWindow = Math.floor(windowSize / 2);
  
  for (let i = 0; i < points.length; i++) {
    let sumX = 0;
    let sumY = 0;
    let count = 0;
    
    for (let j = Math.max(0, i - halfWindow); j <= Math.min(points.length - 1, i + halfWindow); j++) {
      sumX += points[j].x;
      sumY += points[j].y;
      count++;
    }
    
    smoothed.push({
      x: sumX / count,
      y: sumY / count,
      pressure: points[i].pressure,
      timestamp: points[i].timestamp
    });
  }
  
  return smoothed;
}

/**
 * Gaussian smoothing
 */
export function gaussianSmooth(points: Point[], sigma: number = 1.0): Point[] {
  if (points.length < 3) return points;
  
  const kernel = createGaussianKernel(sigma);
  const smoothed: Point[] = [];
  const halfKernel = Math.floor(kernel.length / 2);
  
  for (let i = 0; i < points.length; i++) {
    let sumX = 0;
    let sumY = 0;
    let sumWeight = 0;
    
    for (let j = 0; j < kernel.length; j++) {
      const idx = i - halfKernel + j;
      if (idx >= 0 && idx < points.length) {
        sumX += points[idx].x * kernel[j];
        sumY += points[idx].y * kernel[j];
        sumWeight += kernel[j];
      }
    }
    
    smoothed.push({
      x: sumX / sumWeight,
      y: sumY / sumWeight,
      pressure: points[i].pressure,
      timestamp: points[i].timestamp
    });
  }
  
  return smoothed;
}

function createGaussianKernel(sigma: number): number[] {
  const size = Math.ceil(sigma * 3) * 2 + 1;
  const kernel: number[] = [];
  const mean = size / 2;
  let sum = 0;
  
  for (let x = 0; x < size; x++) {
    const value = Math.exp(-0.5 * Math.pow((x - mean) / sigma, 2)) / (Math.sqrt(2 * Math.PI) * sigma);
    kernel.push(value);
    sum += value;
  }
  
  // Normalize
  return kernel.map(k => k / sum);
}